import { Injectable } from '@angular/core';
import { BdGraph, BdGraphNode, BdGraphStateMap, GraphNodeState } from '../../../../lib/graph/graph.model';
import { ChapterModel } from '../../../story/models/chapter.model';
import { TimelineModel } from '../../../story/models/timeline.model';
import { HotspotModel } from '../../../story/models/labels/hotspot.model';
import { BackgroundModel } from '../../../engine/models/background.model';
import { classToClass } from 'class-transformer';
import { omit, isEmpty, isEqual } from 'lodash';
import { LabelsService } from '../../../story/labels.service';

import { calculateLeafStateMap } from 'src/lib/graph/leaf-state';
import { calculateMiniminumSynchronization } from './minimum-synchronization';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { Store } from '@ngxs/store';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { ColorRecipeModel } from '../../models/color-recipe.model';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { ObjectId } from 'src/app/engine/state/nodes/engine.nodes.state.model';
import { EngineColorRecipeModel } from '../../models/engine-color-recipe.model';
import { hasShownAncestor, hasShownSuccessor } from 'src/app/engine/state/nodes/build-node-state';
import { EngineParticlesState } from 'src/app/engine/state/particles/particles.state';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { VisibilityLayerModel } from 'src/app/story/models/visibility-layers.model';


declare const Human: any;

// rules for propagating state up our BdGraph
class GraphNodeSync {
  shown = 'majorityWins';
  selected = 'majorityWins';
  pickable = 'majorityWins';

  constructor(recipes: string[]) {
    recipes.forEach((recipeName) => {
      this[recipeName] = 'majorityWins';
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class SerializeChapterService {

  constructor(
    private store: Store,
    private labelService: LabelsService,
    private graphService: BdGraphService
  ) { }


  // Query the synchronization and produce the minimum graph synchronization object
  private getMinimumGraphSync(graph: BdGraph<BdGraphNode>, sync: BdGraphStateMap, namespaceMap: { [key: string]: string }): BdGraphStateMap {
    // calculate leaf state based on synchronization


    // TODO: when are recipes defined?
    const recipes = Human.scene.colorRecipes.getRecipes();


    const properties = Object.keys(new GraphNodeState(recipes)); // ["shown", "selected", "pickable"]


    // calculate the state of all leaves given the sync object.
    // leaf state is needed to then propagate back up the tree to build the min sync.
    const leafState = calculateLeafStateMap(graph, sync, properties);

    // minimize sync with inheritable properties: calculate graph state via upward propagation of leaf state
    const syncRules = JSON.parse(JSON.stringify(new GraphNodeSync(recipes)));

    const minSync = calculateMiniminumSynchronization(graph, syncRules, leafState);


    // copy over non-inheritable properties
    Object.entries(sync).forEach(([objectId, objectState]) => {
      const diffSync = omit(objectState, properties);
      if (!isEmpty(diffSync)) {
        minSync[objectId] = { ...minSync[objectId] || {}, ...diffSync };
      }
    });



    const renamedMinSync = new BdGraphStateMap();
    Object.entries(minSync).forEach(([id, syncState]) => {
      renamedMinSync[this.switchNamespace(id, namespaceMap)] = syncState;
    }, this);

    return renamedMinSync;
  }

  // Convert synchronization object to chapter properties
  private convertGraphSync(sync: BdGraphStateMap): any {
    const showObjects = {};
    const selectObjects = {};
    const pickThroughObjects = {};
    const synchronization = {};

    Object.entries(sync).forEach(([id, stateMap]) => {
      Object.entries(stateMap).forEach(([property, value]) => {
        if (property === 'shown') {
          showObjects[id] = value;
        } else if (property === 'selected') {
          selectObjects[id] = value;
        } else if (property === 'pickable') {
          pickThroughObjects[id] = !value;
        } else {
          // keep in the synchronization object directly
          synchronization[id] = { ...synchronization[id] || {}, ...{ [property]: value } };
        }
      }, this);

    }, this);

    return {
      showObjects: showObjects,
      selectObjects: selectObjects,
      pickThroughObjects: pickThroughObjects,
      synchronization: synchronization
    };
  }

  private switchNamespace(id: string, namespaceMap: { [key: string]: string }): string {
    return namespaceMap[id] || id;
  }


  // Serialize timeline maps
  private serializeTimelineMaps(chapter: ChapterModel, namespaceMap: { [key: string]: string }): TimelineModel[] {

    const timelineMaps: TimelineModel[] = chapter.timelineMap;
    timelineMaps.forEach((tmap) => {
      tmap.data.timeline = this.switchNamespace(tmap.data.timeline, namespaceMap);
    });
    return timelineMaps;
  }

  // serialize engine properties
  private serializeProperties(chapter: ChapterModel): any {
    // copy all chapter properties
    const properties: { [key: string]: string; } = {
      ...chapter.properties
    };

    // legacy properties to eventually be discarded
    const annotationLayout = Human.properties.properties['annotations.layout.type'];
    if (annotationLayout !== 'draggable') {
      properties['annotations.layout.type'] = annotationLayout;
    }

    return properties;
  }

  // serialize labels and hotspots
  private serializeHotspots(chapter: ChapterModel, storyLabels: LabelModel[]): { customData: any, hotspots: HotspotModel[] } {

    // initialize hotspots with legacy hotspots.
    // see `fromHumanChapter`
    let hotspots: HotspotModel[] = [...chapter.hotspots]; // these are legacy hotspots, never stored in label state.
    let customData = {};

    const labels = storyLabels.filter(label => label.chapterId === chapter.id);

    labels.forEach((label) => {
      const labelData = this.labelService.serializeLabel(label, hotspots.length);
      hotspots = [...hotspots, ...labelData.hotspots];
      customData = { ...customData, ...labelData.data };
    });

    return {
      customData: customData,
      hotspots: hotspots
    };
  }

  // Serialize the media object in JSON compatible layout
  serializeMedia(chapter: ChapterModel) {
    if (!chapter.media) {
      return null;
    }

    // Chapter / Engine data structure: chapter.media.<clip>.data.{}
    // JSON data structure: chapter.media.<clip>.{}
    return Object.entries(chapter.media).reduce((result, [key, value]) => {
      result[key] = {...value.data};
      return result;
    }, {});
  }

  // serialize background
  private serializeBackground(background: BackgroundModel): BackgroundModel | {} {
    if (!!background) {
      return classToClass(background);
    } else {
      return {};
    }
  }

  // serialize ui
  private serializeUI(huChapter: any, ui: any) {
    if (isEmpty(ui)) {
      return { play: { count: 1 } };
    } else {
      return ui;
    }
  }

  private calcMinimumPaintState(
    graph: BdGraph<BdGraphNode>,
    paintState: Record<ObjectId, EngineColorRecipeModel>
  ): Record<ObjectId, EngineColorRecipeModel> {
    function getPaintState(objectId: ObjectId, includeSelf: boolean = true) {
      const children = graph.successors[objectId] || [];
      const childPaint = children.reduce((acc, childId) => {
        // discard the child's value if its equal to the parent's value.
        // only record children that differ.
        const includeChild = !isEqual(paintState[objectId], paintState[childId]);
        return { ...acc, ...getPaintState(childId, includeChild) };
      }, {});
    
      if (includeSelf) {
        return {
          [objectId]: paintState[objectId],
          ...childPaint,
        };
      }
    
      return childPaint;
    }
    
    return Object.keys(graph.roots).reduce((acc, objectId) => {
      return {...acc, ...getPaintState(objectId)};
    }, {});
  }

  private serializeVisibilityLayers (visibilityLayers: any, graph: BdGraph<BdGraphNode>): any {

    const updateVisibilityLayer = (visibilityLayer: VisibilityLayerModel): VisibilityLayerModel => {
      return {
        ...visibilityLayer,
        objectIds: visibilityLayer.objectIds.filter((objectId) => {
          return graph.nodes.hasOwnProperty(objectId);
        })
      };
    };
    
    const updatedVisibilityLayers = { ...visibilityLayers };

    if (Array.isArray(visibilityLayers.layers)) {

      updatedVisibilityLayers.layers = 
        visibilityLayers.layers
          .map(updateVisibilityLayer)
          .filter((visibilityLayer) => {
            return visibilityLayer.objectIds.length > 0;
          });

    }

    return updatedVisibilityLayers;
  }

  // serialize chapter model into chapter data
  serializeChapter(
    graph: BdGraph<BdGraphNode>,
    chapter: ChapterModel,
    namespaceMap,
    storyLabels: LabelModel[],
    chapterIdMap: Record<string, string>
  ) {

    // Hotspots are generated from labels, so we perform objectID conversion on labels before
    // generating the hotspots. 
    const hotspotData = this.serializeHotspots(chapter, storyLabels);

    const particleSystems = this.store.selectSnapshot(
      EngineParticlesState.particleSystemsForChapter(chapter.id)
    );
    
    const customData: ChapterModel['customData'] = {
      ...chapter.customData, 
      ...hotspotData.customData,
      particleSystems: particleSystems.map((particleSystem) => particleSystem.id)
    };

    // Quiz Questions: serialize w/o chapter id
    const quizQuestions = this.store.selectSnapshot(QuizState.getQuizQuestionsByChapter(chapter.id))
      .map((quizQuestion) => {
        const serializedQuestion = { ...quizQuestion };
        delete serializedQuestion['chapterId'];
        return serializedQuestion;
      });

    const newChapterId = chapterIdMap[chapter.id];

    const chapterData = {
      id: newChapterId,
      displayName: chapter.info.displayName,
      description: chapter.info.description,
      [chapter.camera.action]: chapter.camera.location,
      background: this.serializeBackground(chapter.background),
      hotspots: hotspotData.hotspots,
      clippingPlanes: {clips: chapter.clippingPlanes.clips, boundary: chapter.clippingPlanes.boundary},
      ui: this.serializeUI(chapter, chapter.ui),
      media: this.serializeMedia(chapter),
      graph: chapter.graph || {},
      timelineMap: chapter.timelineMap || [],
      customData: customData,
      viewModes: chapter.viewModes,
      lights: chapter.lights,
      recipes: chapter.recipes || {},
      transparencyMode: chapter.transparencyMode || '',
      fallbackTransparency: chapter.fallbackTransparency || '',
      properties: this.serializeProperties(chapter),
      effects: chapter.effects || {},
      options: chapter.options,
      transitions: chapter.transitions,
      environment: chapter.environment || {},
      legend: chapter.legend || [],
      visibilityLayers: this.serializeVisibilityLayers(chapter.visibilityLayers.src, graph),
      colorRecipeObjects: chapter.colorRecipeObjects,
      contentBlocks: chapter.contentBlocks || [],
      quizQuestions
    };

    const nodeState = this.store.selectSnapshot(EngineNodesState.getAllNodeState);
    const nodeStateForChapter = nodeState[chapter.id];

    const sync = Object.keys(nodeStateForChapter).reduce((acc, objectId) => {
      const state = nodeStateForChapter[objectId];

      const shown = state.shownSetByTween
        ? hasShownAncestor(graph, objectId, chapter.showObjects) ||
          hasShownSuccessor(graph, objectId, chapter.showObjects)
        : state.shown;
    
      acc.boolState[objectId] = { shown, pickable: state.pickable };
      acc.paintState[objectId] = ColorRecipeModel.toEngine(state.paint);
      return acc;
    }, {
      boolState: {},
      paintState: {}
    });

    const minSync = this.getMinimumGraphSync(graph, sync.boolState, namespaceMap);

    Object.entries(this.convertGraphSync(minSync))
      .forEach(([key, val]) => {
        chapterData[key] = val;
      });

    // treat synchronization (paint settings) separately
    // getMinimumGraphSync only considers properties that are boolean.
    chapterData.synchronization = this.calcMinimumPaintState(graph, sync.paintState);

    return chapterData;
  }
  
}

